import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Select, Modal, Form, Input, InputNumber, message, Tabs, Space, Progress, Descriptions, Badge } from 'antd';
import { MailOutlined, FunnelPlotOutlined, PieChartOutlined, ReloadOutlined, PlusOutlined, SendOutlined, CheckCircleOutlined, ClockCircleOutlined, LinkOutlined, CopyOutlined, StarOutlined, StarFilled, CalendarOutlined } from '@ant-design/icons';
import { loiAPI } from '../services/api';

const { Option } = Select;

// Status tag color mapping
const STATUS_CONFIG = {
  '未發送': { color: 'default', label: '未發送' },
  '已發送未讀': { color: 'orange', label: '已發送未讀' },
  '已讀取未填': { color: 'gold', label: '已讀取未填' },
  '已回覆意願': { color: 'green', label: '已回覆意願' },
};

// Priority for default sorting: responded first
const STATUS_PRIORITY = { '已回覆意願': 0, '已讀取未填': 1, '已發送未讀': 2, '未發送': 3 };

// Funnel chart built with CSS bars
const FunnelChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暫無資料</div>;
  }

  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d'];

  return (
    <div style={{ padding: '16px 0' }}>
      {data.map((item, index) => {
        const widthPercent = Math.max((item.value / maxValue) * 100, 8);
        return (
          <div key={item.stage} style={{ marginBottom: 12, textAlign: 'center' }}>
            <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>
              {item.stage}: {item.value}
            </div>
            <div
              style={{
                height: 36,
                width: `${widthPercent}%`,
                margin: '0 auto',
                backgroundColor: colors[index % colors.length],
                borderRadius: 4,
                lineHeight: '36px',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                transition: 'width 0.4s ease',
              }}
            >
              {item.value}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Simple brand interest visualization using Progress circles
const BrandInterestChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暫無資料</div>;
  }

  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const colors = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#f5222d'];

  return (
    <div style={{ padding: '16px 0' }}>
      <Row gutter={[16, 16]} justify="center">
        {data.map((item, index) => {
          const percent = Math.round((item.value / total) * 100);
          return (
            <Col key={item.brand} style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={percent}
                width={100}
                strokeColor={colors[index % colors.length]}
                format={() => `${percent}%`}
              />
              <div style={{ marginTop: 8, fontWeight: 500 }}>{item.brand}</div>
              <div style={{ color: '#999', fontSize: 12 }}>{item.value} 家</div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

const LOIDashboardPage = () => {
  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Dashboard data
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [funnelData, setFunnelData] = useState([]);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [brandInterest, setBrandInterest] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);

  // Filter / UI
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [sendDates, setSendDates] = useState([]);
  const [selectedSendDate, setSelectedSendDate] = useState(null); // null = all dates

  // Create campaign modal
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();

  // ── Load campaigns ──────────────────────────────────────────────
  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const res = await loiAPI.getCampaigns();
      const list = res.data || res;
      setCampaigns(Array.isArray(list) ? list : []);
      // Auto-select: use saved default, or first campaign
      if (!selectedCampaignId && Array.isArray(list) && list.length > 0) {
        const saved = localStorage.getItem('spo_default_campaign_id');
        const savedId = saved ? Number(saved) : null;
        const match = list.find((c) => c.id === savedId);
        setSelectedCampaignId(match ? match.id : list[0].id);
      }
    } catch (err) {
      message.error('載入活動列表失敗: ' + (err.message || '未知錯誤'));
    } finally {
      setCampaignsLoading(false);
    }
  }, [selectedCampaignId]);

  // ── Load send dates ────────────────────────────────────────────
  const loadSendDates = useCallback(async (campaignId) => {
    if (!campaignId) return;
    try {
      const res = await loiAPI.getSendDates(campaignId);
      const list = res.data || res;
      setSendDates(Array.isArray(list) ? list : []);
    } catch {
      setSendDates([]);
    }
  }, []);

  // ── Load dealer status (can be called independently with date filter) ──
  const loadDealerStatus = useCallback(async (campaignId, sendDate) => {
    if (!campaignId) return;
    setDealersLoading(true);
    try {
      const res = await loiAPI.getDealerStatus(campaignId, sendDate || undefined);
      const d = res.data || res;
      setDealers(Array.isArray(d) ? d : []);
    } catch {
      message.error('載入經銷商狀態失敗');
      setDealers([]);
    } finally {
      setDealersLoading(false);
    }
  }, []);

  // ── Load dashboard data for selected campaign ───────────────────
  const loadDashboardData = useCallback(async (campaignId, sendDate) => {
    if (!campaignId) return;

    // Fire all requests in parallel
    setStatsLoading(true);
    setFunnelLoading(true);
    setBrandLoading(true);
    setDealersLoading(true);

    const settle = (promise) => promise.then((r) => r).catch(() => null);

    const [statsRes, funnelRes, brandRes, dealersRes] = await Promise.all([
      settle(loiAPI.getDashboardStats(campaignId)),
      settle(loiAPI.getFunnelData(campaignId)),
      settle(loiAPI.getBrandInterest(campaignId)),
      settle(loiAPI.getDealerStatus(campaignId, sendDate || undefined)),
    ]);

    // Also load send dates
    loadSendDates(campaignId);

    if (statsRes) {
      const d = statsRes.data || statsRes;
      setStats(d);
    } else {
      message.error('載入統計資料失敗');
      setStats(null);
    }
    setStatsLoading(false);

    if (funnelRes) {
      const d = funnelRes.data || funnelRes;
      // API returns {stages: [{name, count}]}, chart expects [{stage, value}]
      const stages = Array.isArray(d) ? d : (d?.stages || []);
      setFunnelData(stages.map((s) => ({ stage: s.name, value: s.count })));
    } else {
      message.error('載入漏斗資料失敗');
      setFunnelData([]);
    }
    setFunnelLoading(false);

    if (brandRes) {
      const d = brandRes.data || brandRes;
      // API returns [{brand_name, count, percentage}], chart expects [{brand, value}]
      const list = Array.isArray(d) ? d : [];
      setBrandInterest(list.map((b) => ({ brand: b.brand_name, value: b.count })));
    } else {
      message.error('載入品牌意願資料失敗');
      setBrandInterest([]);
    }
    setBrandLoading(false);

    if (dealersRes) {
      const d = dealersRes.data || dealersRes;
      setDealers(Array.isArray(d) ? d : []);
    } else {
      message.error('載入經銷商狀態失敗');
      setDealers([]);
    }
    setDealersLoading(false);
  }, [loadSendDates]);

  // ── Effects ─────────────────────────────────────────────────────
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (selectedCampaignId) {
      setSelectedSendDate(null); // reset date filter when campaign changes
      loadDashboardData(selectedCampaignId, null);
    }
  }, [selectedCampaignId, loadDashboardData]);

  // ── Create campaign ─────────────────────────────────────────────
  const handleCreateCampaign = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      await loiAPI.createCampaign(values);
      message.success('活動建立成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      // Reload campaigns and auto-select new one
      const res = await loiAPI.getCampaigns();
      const list = res.data || res;
      setCampaigns(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length > 0) {
        setSelectedCampaignId(list[list.length - 1].id);
      }
    } catch (err) {
      if (err.errorFields) return; // validation error, form handles display
      message.error('建立活動失敗: ' + (err.message || '未知錯誤'));
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Send reminders ──────────────────────────────────────────────
  const handleSendReminders = async () => {
    if (!selectedCampaignId) {
      message.warning('請先選擇活動');
      return;
    }
    Modal.confirm({
      title: '確認催寄',
      content: '將對所有尚未回覆的經銷商發送提醒郵件，確定要繼續嗎？',
      okText: '確認發送',
      cancelText: '取消',
      onOk: async () => {
        setReminderSending(true);
        try {
          await loiAPI.sendReminders(selectedCampaignId);
          message.success('催寄郵件已發送');
          // Refresh data
          loadDashboardData(selectedCampaignId, selectedSendDate);
        } catch (err) {
          message.error('催寄失敗: ' + (err.message || '未知錯誤'));
        } finally {
          setReminderSending(false);
        }
      },
    });
  };

  // ── Handle send date change ────────────────────────────────────
  const handleSendDateChange = (value) => {
    setSelectedSendDate(value || null);
    if (selectedCampaignId) {
      loadDealerStatus(selectedCampaignId, value || null);
    }
  };

  // ── Refresh all ─────────────────────────────────────────────────
  const handleRefresh = () => {
    if (selectedCampaignId) {
      loadDashboardData(selectedCampaignId, selectedSendDate);
    }
  };

  // ── Expandable row for response details ─────────────────────────
  const expandedRowRender = (record) => {
    if (record.status !== '已回覆意願') {
      return <div style={{ color: '#999', padding: 8 }}>尚未回覆意向書</div>;
    }
    return (
      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }} style={{ margin: '8px 0' }}>
        {record.responded_at && (
          <Descriptions.Item label="回覆時間">
            <Badge status="success" text={new Date(record.responded_at).toLocaleString('zh-TW')} />
          </Descriptions.Item>
        )}
        {record.interested_brand_names && record.interested_brand_names.length > 0 && (
          <Descriptions.Item label="有意願品牌">
            {record.interested_brand_names.map((name) => (
              <Tag color="blue" key={name}>{name}</Tag>
            ))}
          </Descriptions.Item>
        )}
        {record.region_type && (
          <Descriptions.Item label="經銷商類型">{record.region_type}</Descriptions.Item>
        )}
        {record.region_areas && (
          <Descriptions.Item label="經銷區域">{record.region_areas}</Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  // ── Table columns ───────────────────────────────────────────────
  const columns = [
    {
      title: '經銷商名稱',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
    },
    {
      title: '聯絡人',
      dataIndex: 'contact_person',
      key: 'contact_person',
    },
    {
      title: 'Email',
      dataIndex: 'contact_email',
      key: 'contact_email',
      ellipsis: true,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || { color: 'default', label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
      filters: Object.entries(STATUS_CONFIG).map(([key, cfg]) => ({ text: cfg.label, value: key })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: '有意願品牌',
      dataIndex: 'interested_brand_names',
      key: 'interested_brand_names',
      render: (brands) => {
        if (!brands || brands.length === 0) return '-';
        return brands.map((name) => <Tag color="blue" key={name}>{name}</Tag>);
      },
    },
    {
      title: '上次活動時間',
      dataIndex: 'last_activity_at',
      key: 'last_activity_at',
      render: (val) => (val ? new Date(val).toLocaleString('zh-TW') : '-'),
      sorter: (a, b) => {
        const ta = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
        const tb = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
        return ta - tb;
      },
    },
    {
      title: '公開連結',
      key: 'links',
      width: 180,
      render: (_, record) => {
        if (!record.token) return '-';
        const base = window.location.origin;
        return (
          <Space size={4}>
            <Button
              size="small"
              type="link"
              icon={<LinkOutlined />}
              onClick={() => window.open(`${base}/survey/${record.token}`, '_blank')}
              title="開啟意向書"
            >
              意向書
            </Button>
            <Button
              size="small"
              type="link"
              icon={<LinkOutlined />}
              onClick={() => window.open(`${base}/prices/${record.token}`, '_blank')}
              title="開啟價格清單"
            >
              價格
            </Button>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              title="複製連結"
              onClick={() => {
                const links = `意向書: ${base}/survey/${record.token}\n價格清單: ${base}/prices/${record.token}`;
                navigator.clipboard.writeText(links).then(
                  () => message.success(`已複製 ${record.company_name} 的連結`),
                  () => message.error('複製失敗')
                );
              }}
            />
          </Space>
        );
      },
    },
  ];

  // ── Filtered dealers ────────────────────────────────────────────
  const filteredDealers = dealers
    .filter((d) => statusFilter === 'all' || d.status === statusFilter)
    .filter((d) => !searchText || d.company_name?.includes(searchText) || d.contact_person?.includes(searchText) || d.contact_email?.toLowerCase().includes(searchText.toLowerCase()))
    .sort((a, b) => {
      // Sort by status priority (responded first), then by activity time desc
      const pa = STATUS_PRIORITY[a.status] ?? 9;
      const pb = STATUS_PRIORITY[b.status] ?? 9;
      if (pa !== pb) return pa - pb;
      const ta = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
      const tb = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
      return tb - ta;
    });

  // ── Recent responses (dealers who responded, sorted by time) ────
  const recentResponses = dealers
    .filter((d) => d.status === '已回覆意願' && d.responded_at)
    .sort((a, b) => new Date(b.responded_at).getTime() - new Date(a.responded_at).getTime())
    .slice(0, 10);

  // ── Tab items for status filter ─────────────────────────────────
  const statusTabs = [
    { key: 'all', label: `全部 (${dealers.length})`, children: null },
    ...Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
      const count = dealers.filter((d) => d.status === key).length;
      return { key, label: `${cfg.label} (${count})`, children: null };
    }),
  ];

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      {/* ── Top: Campaign Selector ──────────────────────────────── */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space>
              <span style={{ fontWeight: 600, fontSize: 16 }}>LOI 意向書管理</span>
              <Select
                style={{ width: 360 }}
                placeholder="選擇活動"
                value={selectedCampaignId}
                onChange={(val) => setSelectedCampaignId(val)}
                loading={campaignsLoading}
                allowClear={false}
              >
                {campaigns.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.case_number ? `[${c.case_number}] ` : ''}{c.title}{c.year ? ` (${c.year})` : ''}
                    {Number(localStorage.getItem('spo_default_campaign_id')) === c.id && (
                      <Tag color="gold" style={{ marginLeft: 8, fontSize: 11 }}>預設</Tag>
                    )}
                  </Option>
                ))}
              </Select>
              <Button
                size="small"
                icon={Number(localStorage.getItem('spo_default_campaign_id')) === selectedCampaignId ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                onClick={() => {
                  localStorage.setItem('spo_default_campaign_id', String(selectedCampaignId));
                  message.success('已設為預設活動');
                  setCampaigns([...campaigns]); // force re-render
                }}
              >
                {Number(localStorage.getItem('spo_default_campaign_id')) === selectedCampaignId ? '預設' : '設為預設'}
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={!selectedCampaignId}>
                重新整理
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                建立新活動
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Stats Cards ─────────────────────────────────────────── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card loading={statsLoading}>
            <Statistic title="總經銷商數" value={stats?.total_dealers ?? 0} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="已發送數"
              value={stats?.total_sent ?? 0}
              prefix={<MailOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="已開啟數"
              value={stats?.total_opened ?? 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="已回覆數"
              value={stats?.total_responded ?? 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Recent Responses ──────────────────────────────────────── */}
      {recentResponses.length > 0 && (
        <Card
          title={
            <span><ClockCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />最新回覆紀錄</span>
          }
          style={{ marginBottom: 16 }}
          bodyStyle={{ padding: '12px 24px' }}
        >
          {recentResponses.map((d) => (
            <div
              key={d.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div>
                <Badge status="success" />
                <strong style={{ marginLeft: 4 }}>{d.company_name}</strong>
                {d.contact_person && <span style={{ color: '#666', marginLeft: 8 }}>({d.contact_person})</span>}
                {d.interested_brand_names && d.interested_brand_names.length > 0 && (
                  <span style={{ marginLeft: 12 }}>
                    {d.interested_brand_names.map((name) => (
                      <Tag color="blue" key={name} style={{ marginRight: 4 }}>{name}</Tag>
                    ))}
                  </span>
                )}
              </div>
              <div style={{ color: '#999', fontSize: 13 }}>
                {d.responded_at ? new Date(d.responded_at).toLocaleString('zh-TW') : ''}
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* ── Charts Row ──────────────────────────────────────────── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card
            title={
              <span><FunnelPlotOutlined style={{ marginRight: 8 }} />漏斗圖</span>
            }
            loading={funnelLoading}
          >
            <FunnelChart data={funnelData} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <span><PieChartOutlined style={{ marginRight: 8 }} />品牌意願分佈</span>
            }
            loading={brandLoading}
          >
            <BrandInterestChart data={brandInterest} />
          </Card>
        </Col>
      </Row>

      {/* ── Dealer Status Table ─────────────────────────────────── */}
      <Card
        title="經銷商狀態"
        extra={
          <Button
            type="primary"
            danger
            icon={<SendOutlined />}
            loading={reminderSending}
            onClick={handleSendReminders}
            disabled={!selectedCampaignId}
          >
            一鍵催寄
          </Button>
        }
      >
        {/* Send date filter */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <Space>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontWeight: 500, fontSize: 13 }}>發送批次：</span>
            <Select
              placeholder="全部日期"
              value={selectedSendDate}
              onChange={handleSendDateChange}
              allowClear
              style={{ width: 240 }}
            >
              {sendDates.map((item) => (
                <Option key={item.date} value={item.date}>
                  {item.date}（{item.dealer_count} 位經銷商）
                </Option>
              ))}
            </Select>
          </Space>
          {selectedSendDate && (
            <Tag color="blue" style={{ fontSize: 13 }}>
              篩選：{selectedSendDate} 發送的經銷商
            </Tag>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Tabs
            activeKey={statusFilter}
            onChange={(key) => setStatusFilter(key)}
            items={statusTabs}
            style={{ marginBottom: 0 }}
          />
          <Input.Search
            placeholder="搜尋公司名稱 / 聯絡人 / Email"
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
          />
        </div>
        <Table
          columns={columns}
          dataSource={filteredDealers}
          loading={dealersLoading}
          rowKey={(record) => record.id || record.contact_email || Math.random()}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
          size="middle"
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => record.status === '已回覆意願',
          }}
        />
      </Card>

      {/* ── Create Campaign Modal ───────────────────────────────── */}
      <Modal
        title="建立新活動"
        open={createModalVisible}
        onOk={handleCreateCampaign}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        confirmLoading={createLoading}
        okText="建立"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="case_number"
            label="案號"
            rules={[{ required: true, message: '請輸入案號' }]}
          >
            <Input placeholder="例: LOI-2026-001" />
          </Form.Item>
          <Form.Item
            name="title"
            label="活動名稱"
            rules={[{ required: true, message: '請輸入活動名稱' }]}
          >
            <Input placeholder="例: 2026年度經銷意向調查" />
          </Form.Item>
          <Form.Item
            name="year"
            label="年度"
            rules={[{ required: true, message: '請輸入年度' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={2020}
              max={2099}
              placeholder="例: 2026"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LOIDashboardPage;
